'use client';

import { useEffect, useState } from 'react';
import styles from './landingHeroCarousel.module.scss';
import { AnimatePresence, motion } from 'framer-motion';

const BASE_IMAGE_URL = '/hero/partner-logos/';

const FALLBACK_LOGOS = [
  'freedlending.png',
  '42solutions.png',
  'covelending.png',
  'ushjo.png',
  'casafinance.png',
  '890capital.png',
  'acme.png'
];

export default function LandingHeroCarousel({
  visible = 6,
  logos = FALLBACK_LOGOS.map((logo) => ({
    url: `${BASE_IMAGE_URL}${logo}`,
    alternativeText: ''
  }))
}: {
  visible?: number;
  logos: { url: string; alternativeText: string }[];
}) {
  const initialVisible = logos.slice(0, visible);
  const initialHidden = logos.slice(visible);
  const [visibleLogos, setVisibleLogos] = useState(initialVisible);
  const [hiddenLogos, setHiddenLogos] = useState(initialHidden);
  const [lastSwappedIndex, setLastSwappedIndex] = useState<number | null>(null);

  useEffect(() => {
    const swapRandomLogo = () => {
      if (hiddenLogos.length === 0 || visibleLogos.length === 0) return;

      let visibleIndex = Math.floor(Math.random() * visibleLogos.length);
      while (visibleIndex === lastSwappedIndex && visibleLogos.length > 1) {
        visibleIndex = Math.floor(Math.random() * visibleLogos.length);
      }
      const hiddenIndex = Math.floor(Math.random() * hiddenLogos.length);

      const removedLogo = visibleLogos[visibleIndex];
      const newLogo = hiddenLogos[hiddenIndex];
      if (!removedLogo || !newLogo) return;

      const newVisible = [...visibleLogos];
      newVisible.splice(visibleIndex, 1, newLogo);
      setVisibleLogos(newVisible);
      const updatedHidden = [...hiddenLogos];
      updatedHidden[hiddenIndex] = removedLogo;
      setHiddenLogos(updatedHidden);
      setLastSwappedIndex(visibleIndex);
    };

    const interval = setInterval(swapRandomLogo, 3000);
    return () => clearInterval(interval);
  }, [visibleLogos, hiddenLogos, lastSwappedIndex]);

  return (
    <ul className={styles.carousel}>
      <AnimatePresence initial={false} mode="popLayout">
        {visibleLogos.map((logo) => (
          <li key={logo.url} className={styles.logoWrapper}>
            <motion.div
              className={styles.logoContainer}
              key={logo.url}
              initial={{
                opacity: 0,
                y: '-100%'
              }}
              animate={{ opacity: 1, y: 0 }}
              exit={{
                opacity: 0,
                y: '100%',
                transition: { duration: 0.3 }
              }}
              transition={{
                type: 'spring',
                damping: 20,
                mass: 1.5
              }}
            >
              <img
                className={styles.logoImage}
                src={logo.url}
                alt={logo.alternativeText}
              />
            </motion.div>
          </li>
        ))}
      </AnimatePresence>
    </ul>
  );
}
